import { Component, computed, DestroyRef, effect, inject, input, model, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { provideIcons } from '@ng-icons/core';
import { lucideListFilter, lucidePlus, lucideSearch, lucideX } from '@ng-icons/lucide';
import { TrierParPipe } from '@shared/pipes/trier-par.pipe';
import { BrnSelectImports } from '@spartan-ng/brain/select';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmDialogService } from '@spartan-ng/helm/dialog';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { HlmInputGroupImports } from '@spartan-ng/helm/input-group';
import { HlmSelectImports } from '@spartan-ng/helm/select';

import { Site } from '../../administration/gestion-sites/model/site.model';
import { Cartographie } from '../../cartographie/models/cartographie.model';
import { CartographieStore } from '../../cartographie/store/cartographie.store';
import { SidepanelService } from '../../shared/components/sidepanel/sidepanel.service';
import { BadgeDirective } from '../../shared/directives/badge.directive';
import { FiltresCatalogueNational } from '../catalogue-national.component';
import { DialogSelectCfaComponent } from '../components/dialog-select-cfa/dialog-select-cfa.component';
import { FiltresCatalogueNationalComponent } from '../filtres-catalogue-national/filtres-catalogue-national.component';

@Component({
  selector: 'app-header-catalogue-national',
  imports: [
    BrnSelectImports,
    HlmSelectImports,
    HlmButtonImports,
    HlmInputGroupImports,
    FormsModule,
    HlmIconImports,
    BadgeDirective,
    TrierParPipe,
  ],
  templateUrl: './header-catalogue-national.component.html',
  providers: [
    provideIcons({
      lucideSearch,
      lucideX,
      lucidePlus,
      lucideListFilter,
    }),
  ],
})
export class HeaderCatalogueNationalComponent {
  readonly #cartographieStore = inject(CartographieStore);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #router = inject(Router);
  readonly #dialog = inject(HlmDialogService);
  readonly #destroyRef = inject(DestroyRef);

  readonly filtres = input<FiltresCatalogueNational>();

  readonly filtrer = output<string>();

  protected readonly cartographie = computed(() => this.#cartographieStore.selectedEntity());
  protected readonly cartographies = computed(() => this.#cartographieStore.actives());
  readonly nbFiltres = computed(() => Object.values(this.filtres() ?? {}).filter((x) => !!x).length);

  formation = model<string>('');

  constructor() {
    effect(() => {
      const cartographiesActives = this.#cartographieStore.actives();
      if (!this.#cartographieStore.selectedEntity() && cartographiesActives.length > 0) {
        const cartographiesTriées = cartographiesActives.sort((c1, c2) => c1.nom.localeCompare(c2.nom));
        const cartoLaPlusAvancéeMaisPasTerminée = cartographiesTriées[0];
        this.#cartographieStore.select({ id: cartoLaPlusAvancéeMaisPasTerminée.id });
        this.#router.navigate(['cartographies', cartoLaPlusAvancéeMaisPasTerminée.slug, 'catalogue-national']);
      }
    });

    effect(() => this.filtrer.emit(this.formation()));
  }

  setCartographieCourante(cartographie: Cartographie) {
    this.#cartographieStore.select({ id: cartographie.id });
    this.#router.navigate(['cartographies', cartographie.slug, 'catalogue-national']);
  }

  clearFormation() {
    this.formation.set('');
  }

  selectionnerSite() {
    const dialogRef = this.#dialog.open(DialogSelectCfaComponent, {
      context: {
        cartographie: this.cartographie(),
      },
      contentClass: 'sm:!max-w-[500px]',
    });
    dialogRef.closed$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((site?: Site) => {
      if (!site) return;

      this.#router.navigate(['cartographies', this.cartographie()?.slug, 'offre-régionale', site.id]);
    });
  }

  openFiltres() {
    const cartographie = this.cartographie();
    if (cartographie) {
      this.#sidepanelService.setComponentToDisplay(
        'Filtrer',
        FiltresCatalogueNationalComponent,
        `Vous pouvez filtrer les éléments qui seront affichés dans le catalogue national pour faciliter votre recherche.`,
        { idCartographie: cartographie.id, filtres: this.filtres() },
      );
      this.#sidepanelService.open();
    }
  }
}
